import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { FileItemThumbnail } from '@provision/rich-text-editor';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { Conversation } from 'src/graphql';
import {
  ContactSupportInput,
  SupportIssueType,
  useContactSupport,
} from 'src/graphql/entities/contact-support';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { LinkConversationData, SearchConversationField } from './search-conversation-field';

export type ContactSupportFormValues = Pick<
  ContactSupportInput,
  'issueType' | 'additionalContext' | 'subject'
> & {
  attachments: FileList | null;
  linkedConversation: LinkConversationData | null;
};

const MAX_ATTACHMENTS = 3;

export const ContactSupportModal = () => {
  const { closeModal } = useModalMethods(Modals.ContactSupportModal);
  const { isOpened } = useModalState(Modals.ContactSupportModal);
  const { contactSupport } = useContactSupport();
  const [fileLimitExceeded, setFileLimitExceeded] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ContactSupportFormValues>({
    defaultValues: {
      issueType: SupportIssueType.GENERAL_QUESTION,
      attachments: null,
      additionalContext: '',
      linkedConversation: null,
    },
  });

  const attachments: FileList | null = watch('attachments');
  const issueType = watch('issueType');

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const onSubmit = useCallback(
    async (data: ContactSupportFormValues) => {
      closeModal();
      await contactSupport({
        ...data,
        linkedConversation: data.linkedConversation,
      });
    },
    [closeModal, contactSupport],
  );

  const handleClose = useCallback(() => {
    reset();
    setFileLimitExceeded(false);
    closeModal();
  }, [closeModal, reset]);

  const removeAttachment = useCallback(
    (index: number) => {
      if (!attachments) return;

      const newAttachments = Array.from(attachments);
      newAttachments.splice(index, 1);

      const dataTransfer = new DataTransfer();
      newAttachments.forEach((file) => {
        dataTransfer.items.add(file);
      });

      setValue('attachments', dataTransfer.files);
      setFileLimitExceeded(false);
    },
    [attachments, setValue],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;

      const currentAttachments = attachments?.length || 0;
      if (currentAttachments + files.length > MAX_ATTACHMENTS) {
        setFileLimitExceeded(true);
        return;
      }

      const newAttachments = Array.from(files || []);

      // For some reason, passing the files directly to setValue doesn't work
      // so we have to create a new DataTransfer object and add the files to it
      const dataTransfer = new DataTransfer();
      Array.from(attachments || []).forEach((file) => {
        dataTransfer.items.add(file);
      });
      newAttachments.forEach((file) => {
        dataTransfer.items.add(file);
      });

      setValue('attachments', dataTransfer.files);
      setFileLimitExceeded(false);
    },
    [attachments, setValue],
  );

  const onSearchConversationFieldChange = useCallback(
    (conversation: Conversation) => {
      setValue('linkedConversation', {
        friendlyId: conversation.friendlyId,
        subject: conversation.subject,
        conversationId: conversation.conversationId,
        partnerId: conversation.partnerId,
      });
    },
    [setValue],
  );

  return (
    <Dialog open={isOpened} onClose={handleClose} fullWidth>
      <DialogTitle>
        <Typography>Contact Support</Typography>
      </DialogTitle>
      <DialogContent>
        <Box py={1}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl fullWidth margin="normal" error={!!errors.issueType}>
              <InputLabel id="issueType-label">Issue Type</InputLabel>
              <Select
                labelId="issueType-label"
                id="issueType"
                label="Issue Type"
                defaultValue={SupportIssueType.GENERAL_QUESTION}
                {...register('issueType', { required: true })}
              >
                {Object.values(SupportIssueType)
                  .sort()
                  .map((issueType) => (
                    <MenuItem key={issueType} value={issueType}>
                      {issueType}
                    </MenuItem>
                  ))}
              </Select>
              {!!errors.issueType && <p>This field is required</p>}
            </FormControl>

            <TextField
              id="subject"
              label="Subject"
              fullWidth
              margin="normal"
              {...register('subject', { required: false })}
            />

            <TextField
              id="additionalContext"
              label="Additional Context"
              multiline
              rows={4}
              fullWidth
              margin="normal"
              {...register('additionalContext', { required: true })}
              error={!!errors.additionalContext}
              helperText={errors.additionalContext ? 'This field is required' : ''}
            />

            <SearchConversationField
              control={control}
              setValue={onSearchConversationFieldChange}
              requiredValidation={{
                message: 'This field is required',
                value: issueType === SupportIssueType.CONVERSATION_QA_REQUEST,
              }}
            />
            <Button variant="text" component="label">
              <DynamicIcon name="upload" />
              Attach a file
              <input
                type="file"
                hidden
                multiple
                {...register('attachments')}
                onChange={handleFileChange}
              />
            </Button>
            {fileLimitExceeded ? (
              <Typography color="error">You can only upload up to 3 files.</Typography>
            ) : null}
            {!!attachments?.length &&
              Array.from(attachments).map((file, index) => (
                <Box key={file.name + index.toString()} sx={{ marginBottom: '.5rem' }}>
                  <FileItemThumbnail
                    index={index}
                    file={file}
                    onDelete={(index) => removeAttachment(index)}
                  />
                </Box>
              ))}
            <Button
              disabled={isSubmitting}
              variant="text"
              color="primary"
              type="submit"
              sx={{ marginTop: '2rem', float: 'right' }}
            >
              Submit
            </Button>
          </form>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
